import { CheckCircle, Clock, User } from 'lucide-react'
import { Timestamp } from 'firebase/firestore'

interface Vote {
  userId: string
  userName?: string
  userPhoto?: string
  timestamp: Timestamp
  type: 'confirmed' | 'outdated'
}

interface UserPOIVoteHistoryProps {
  confirmations: { 
    [userId: string]: {
      timestamp: Timestamp
      userName?: string
      userPhoto?: string
    } | Timestamp // Handle backward compatibility
  }
  outdatedVotes: { 
    [userId: string]: {
      timestamp: Timestamp
      userName?: string
      userPhoto?: string
    } | Timestamp // Handle backward compatibility
  }
  getUserName?: (userId: string) => string
}

export function UserPOIVoteHistory({
  confirmations,
  outdatedVotes,
  getUserName
}: UserPOIVoteHistoryProps) {
  // Combine all votes with their types
  const allVotes: Vote[] = []
  
  // Add confirmations
  Object.entries(confirmations || {}).forEach(([userId, voteData]) => {
    // Handle backward compatibility
    const isTimestamp = voteData && typeof voteData.toDate === 'function'
    const timestamp = isTimestamp ? voteData as Timestamp : voteData.timestamp
    const userName = isTimestamp ? getUserName?.(userId) : voteData.userName || getUserName?.(userId)
    const userPhoto = isTimestamp ? undefined : voteData.userPhoto
    
    allVotes.push({
      userId,
      userName,
      userPhoto,
      timestamp,
      type: 'confirmed'
    })
  })
  
  // Add outdated votes
  Object.entries(outdatedVotes || {}).forEach(([userId, voteData]) => {
    // Handle backward compatibility
    const isTimestamp = voteData && typeof voteData.toDate === 'function'
    const timestamp = isTimestamp ? voteData as Timestamp : voteData.timestamp
    const userName = isTimestamp ? getUserName?.(userId) : voteData.userName || getUserName?.(userId)
    const userPhoto = isTimestamp ? undefined : voteData.userPhoto
    
    allVotes.push({
      userId,
      userName,
      userPhoto,
      timestamp,
      type: 'outdated'
    })
  })
  
  // Sort by timestamp (newest first)
  allVotes.sort((a, b) => {
    const timeA = a.timestamp?.toMillis() || 0
    const timeB = b.timestamp?.toMillis() || 0
    return timeB - timeA
  })
  
  const formatDate = (timestamp: any) => {
    if (!timestamp) return ''
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    
    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    })
  }
  
  if (allVotes.length === 0) return null
  
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
        Community Votes
      </h4>
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {allVotes.map((vote, index) => (
          <div key={`${vote.userId}-${vote.type}-${index}`} className="flex items-center gap-3 text-sm">
            <div className="flex-shrink-0">
              {vote.userPhoto ? (
                <img
                  src={vote.userPhoto}
                  alt={vote.userName || 'User'}
                  className="w-5 h-5 rounded-full"
                />
              ) : (
                <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center">
                  <User className="w-3 h-3 text-gray-400" />
                </div>
              )}
            </div>
            
            <div className="flex-1 flex items-center gap-2">
              <span className="text-gray-300">
                {vote.userName || 'Anonymous'}
              </span>
              <span className="text-gray-500">
                {vote.type === 'confirmed' ? (
                  <span className="flex items-center gap-1 text-green-500">
                    <CheckCircle className="w-3 h-3" />
                    confirmed
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-500">
                    <Clock className="w-3 h-3" />
                    marked outdated
                  </span>
                )}
              </span>
            </div>
            
            <span className="text-xs text-gray-500">
              {formatDate(vote.timestamp)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}